import { Node, mergeAttributes } from '@tiptap/core';

export interface IfBlockOptions {
    getVariables: () => { id: string; name: string }[];
}

export const IfBlock = Node.create<IfBlockOptions>({

    name: 'ifBlock',

    group: 'block',

    content: 'block+',

    addOptions() {
        return {
            getVariables: () => []
        };
    },

    addAttributes() {
        return {
            field: {
                default: ''
            },
            operator: {
                default: '=='
            },
            value: {
                default: ''
            },
            // '' = paragrafo normale, '1' = l'app toglie il <p> (un solo paragrafo),
            // 'join' = l'app attacca il contenuto al paragrafo precedente
            bare: {
                default: '',
                parseHTML: (el: HTMLElement) => el.getAttribute('bare') ?? '',
                renderHTML: (attrs: Record<string, any>) => attrs['bare'] ? { bare: attrs['bare'] } : {}
            }
        };
    },

    addKeyboardShortcuts() {
        return {
            // Invio su un paragrafo vuoto in fondo al blocco: esce dal blocco
            Enter: () => {
                const { $from, empty } = this.editor.state.selection;
                if (!empty || $from.depth < 2) {
                    return false;
                }
                const parent = $from.parent;
                const container = $from.node($from.depth - 1);
                const isLastChild = $from.index($from.depth - 1) === container.childCount - 1;
                if (container.type.name !== 'ifBlock' || !parent.isTextblock || parent.content.size > 0 || !isLastChild || container.childCount < 2) {
                    return false;
                }
                return this.editor.commands.liftEmptyBlock();
            }
        };
    },

    parseHTML() {
        return [
            {
                tag: 'if-block:not([inline])'
            }
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'if-block',
            mergeAttributes(HTMLAttributes),
            0
        ];
    },

    addNodeView() {

        const getVariables = this.options.getVariables;

        return (props) => {

            const { node, editor, getPos } = props;

            // Aggiorna gli attributi per posizione: updateAttributes('ifBlock') agisce solo
            // se il cursore e' dentro il blocco, cosa che non vale usando i controlli dell'header
            const updateAttributes = (attrs: any) => {
                const pos = typeof getPos === 'function' ? getPos() : undefined;
                if (pos === undefined) {
                    return;
                }
                const current = editor.state.doc.nodeAt(pos);
                if (!current || current.type.name !== 'ifBlock') {
                    return;
                }
                editor.view.dispatch(
                    editor.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, ...attrs })
                );
            };

            const dom = document.createElement('div');
            dom.className = 'if-block';

            // =========================
            // HEADER (CONDITIONE)
            // =========================

            const header = document.createElement('div');
            header.className = 'if-header';

            // FIELD
            const field = document.createElement('select');

            const fields = getVariables();
            if (node.attrs['field'] && !fields.some(f => f.id === node.attrs['field'])) {
                fields.push({ id: node.attrs['field'], name: node.attrs['field'] });
            }

            fields.forEach(f => {
                const opt = document.createElement('option');
                opt.value = f.id;
                opt.innerText = f.name;
                field.appendChild(opt);
            });

            field.value = node.attrs['field'];

            field.onchange = (e: any) => {
                updateAttributes({
                    field: e.target.value
                });
            };

            // OPERATOR
            const operator = document.createElement('select');

            ['==', '!=', '<=', '>=', '<', '>', 'contains'].forEach(op => {
                const opt = document.createElement('option');
                opt.value = op;
                opt.innerText = op;
                operator.appendChild(opt);
            });

            operator.value = node.attrs['operator'];

            operator.onchange = (e: any) => {
                updateAttributes({
                    operator: e.target.value
                });
            };

            // VALUE
            const value = document.createElement('input');

            let localValue = node.attrs['value'] ?? '';
            let timeout: any;

            value.placeholder = 'value';
            value.value = localValue;

            value.oninput = (e: any) => {
                localValue = (e.target as HTMLInputElement).value;

                clearTimeout(timeout);

                timeout = setTimeout(() => {
                    updateAttributes({
                        value: localValue
                    });
                }, 300);
            };

            // PARAGRAFO IN STAMPA
            const bare = document.createElement('select');
            bare.title = 'Come esce il contenuto in stampa';
            [
                { value: '', label: 'Paragrafo normale' },
                { value: '1', label: 'Senza ¶' },
                { value: 'join', label: 'Unito al precedente' }
            ].forEach(o => {
                const opt = document.createElement('option');
                opt.value = o.value;
                opt.innerText = o.label;
                bare.appendChild(opt);
            });

            bare.value = node.attrs['bare'] || '';

            bare.onchange = (e: any) => {
                updateAttributes({
                    bare: e.target.value
                });
            };

            header.appendChild(field);
            header.appendChild(operator);
            header.appendChild(value);
            header.appendChild(bare);

            dom.appendChild(header);

            // =========================
            // TRUE CONTENT
            // =========================

            const trueBlock = document.createElement('div');
            trueBlock.className = 'if-true-block';

            const trueLabel = document.createElement('div');
            trueLabel.innerText = ' ';

            trueLabel.style.fontSize = '12px';
            trueLabel.style.opacity = '0.6';

            trueBlock.appendChild(trueLabel);

            const trueContent = document.createElement('div');
            trueContent.className = 'if-content';

            trueBlock.appendChild(trueContent);

            dom.appendChild(trueBlock);

            // =========================
            // NODEVIEW RETURN
            // =========================

            const ensureFieldOption = (id: string) => {
                if (id && !Array.from(field.options).some(o => o.value === id)) {
                    const opt = document.createElement('option');
                    opt.value = id;
                    opt.innerText = id;
                    field.appendChild(opt);
                }
            };

            return {
                dom,
                contentDOM: trueContent,

                // Aggiorna i controlli senza ricostruire il nodo (si manterrebbe il focus dell'input)
                update: (updatedNode: any) => {
                    if (updatedNode.type !== node.type) {
                        return false;
                    }
                    ensureFieldOption(updatedNode.attrs['field']);
                    field.value = updatedNode.attrs['field'];
                    operator.value = updatedNode.attrs['operator'];
                    bare.value = updatedNode.attrs['bare'] || '';
                    if (document.activeElement !== value) {
                        value.value = updatedNode.attrs['value'] ?? '';
                    }
                    return true;
                },

                // Gli eventi dei controlli dell'header sono gestiti dai controlli, non da ProseMirror
                stopEvent: (event: Event) => header.contains(event.target as any),
                ignoreMutation: (mutation: any) => header.contains(mutation.target as any)
            };
        };
    }

});